import React from "react";
import { Button, Card, Badge } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

type Rule = {
  ifConditions: {
    field: string;
    operator: string;
    value: { keywords?: string[]; amount?: number };
  }[];
  thenSet: { actionType: string; value: string }[];
  createdAt: string;
};

type Props = {
  rules: Rule[];
  onEdit: (index: number) => void;
  onRemove: (index: number) => void;
};

const TagRegexesTransactionViewOnly: React.FC<Props> = ({
  rules,
  onEdit,
  onRemove,
}) => {
  const formatCondition = (condition: Rule["ifConditions"][0]) => {
    const fieldLabel =
      {
        description: "Description",
        amount: "Amount",
        date: "Date",
        balance: "Balance",
        account: "Account",
      }[condition.field] || condition.field;

    const operatorLabel =
      {
        containsAny: "contains",
        doesNotContainAny: "does not contain",
        equal: "equals",
        notEqual: "does not equal",
        greaterThan: ">",
        lessThan: "<",
        greaterThanOrEqual: ">=",
        lessThanOrEqual: "<=",
      }[condition.operator] || condition.operator;

    let valueStr = "";
    if (condition.field === "amount" || condition.field === "balance") {
      valueStr = String(condition.value.amount || 0);
    } else if (condition.value.keywords) {
      valueStr = condition.value.keywords.join(", ");
    }

    return `${fieldLabel} ${operatorLabel} "${valueStr}"`;
  };

  if (rules.length === 0) {
    return (
      <Card className="text-center">
        <Card.Body>
          <p>No transaction tagging rules have been created yet.</p>
          <p className="text-muted">
            Create rules to automatically tag transactions based on conditions
            like amount, description, or account.
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div>
      {rules.map((rule, index) => (
        <Card key={index} className="mb-3">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-start">
              <div className="flex-grow-1">
                <h6 className="mb-2">
                  Rule #{index + 1}
                  <small className="text-muted ml-2">
                    Created: {new Date(rule.createdAt).toLocaleDateString()}
                  </small>
                </h6>

                <div className="mb-2">
                  <strong>If:</strong>
                  <div className="ml-3">
                    {rule.ifConditions.map((condition, condIndex) => (
                      <div key={condIndex} className="mb-1">
                        {condIndex > 0 && (
                          <Badge variant="secondary" className="mr-2">
                            AND
                          </Badge>
                        )}
                        <code>{formatCondition(condition)}</code>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <strong>Then:</strong>
                  <div className="ml-3">
                    {rule.thenSet.map((action, actionIndex) => (
                      <div key={actionIndex} className="mb-1">
                        Tag transaction with{" "}
                        <Badge variant="primary">{action.value}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="ml-3">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => onEdit(index)}
                  className="mr-2"
                >
                  <FontAwesomeIcon icon="edit" />
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => onRemove(index)}
                >
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      ))}
    </div>
  );
};

export default TagRegexesTransactionViewOnly;
